import { useNavigate } from "react-router-dom";

/* ─── Inline SVG icons ─── */
const IconDraw = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M6 26l4-1.5L25.5 9a2 2 0 000-2.83l-.17-.17a2 2 0 00-2.83 0L7 21.5 6 26z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 9l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const IconSparkle = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M16 4l2.5 8.5L27 16l-8.5 3.5L16 28l-2.5-8.5L5 16l8.5-3.5L16 4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="25" cy="7" r="1.5" fill="currentColor" opacity="0.5" />
    <circle cx="7" cy="25" r="1" fill="currentColor" opacity="0.4" />
  </svg>
);
const IconBook = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M5 6.5C5 5.67 5.67 5 6.5 5H13c1.66 0 3 1.34 3 3v18.5a2.5 2.5 0 00-2.5-2.5H5V6.5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M27 6.5c0-.83-.67-1.5-1.5-1.5H19c-1.66 0-3 1.34-3 3v18.5a2.5 2.5 0 012.5-2.5H27V6.5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const IconCamera = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="4" y="9" width="24" height="17" rx="3" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="16" cy="17" r="4.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M11 9l1.5-3h7L21 9" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const IconShield = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M16 4l10 4v8c0 6-4.5 10.5-10 12.5C10.5 26.5 6 22 6 16V8l10-4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M12 16l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconHeart = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M16 28S4 20 4 13a6.5 6.5 0 0112-3.5A6.5 6.5 0 0128 13c0 7-12 15-12 15z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const IconChat = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M6 8a3 3 0 013-3h14a3 3 0 013 3v10a3 3 0 01-3 3H13l-5 4v-4H9a3 3 0 01-3-3V8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="12" cy="13" r="1.2" fill="currentColor" />
    <circle cx="16" cy="13" r="1.2" fill="currentColor" />
    <circle cx="20" cy="13" r="1.2" fill="currentColor" />
  </svg>
);
const IconBell = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M16 4a8 8 0 00-8 8c0 4-2 8-2 8h20s-2-4-2-8a8 8 0 00-8-8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M13.5 26a3 3 0 005 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const IconCheck = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M5 9l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconArrow = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="transition-transform group-hover:translate-x-0.5">
    <path d="M7 5l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ─── Data ─── */
const COLORS = {
  primary: { bg: "rgba(196,112,63,0.1)", text: "#C4703F" },
  accent: { bg: "rgba(212,168,83,0.1)", text: "#D4A853" },
  secondary: { bg: "rgba(91,138,114,0.1)", text: "#5B8A72" },
  rose: { bg: "rgba(181,99,106,0.1)", text: "#B5636A" },
};

const STEPS = [
  {
    num: "01",
    icon: <IconDraw />,
    title: "Draw a creature",
    desc: "Scribble on the canvas or snap a photo of a paper doodle. Stick figures welcome — the messier the better.",
    color: COLORS.primary,
  },
  {
    num: "02",
    icon: <IconSparkle />,
    title: "AI brings it to life",
    desc: "Answer three fun questions about your creature's personality. Our AI renders it as a photorealistic plush toy, crystal figure, or whatever the doodle suggests.",
    color: COLORS.accent,
  },
  {
    num: "03",
    icon: <IconBook />,
    title: "A story unfolds",
    desc: "Your creature embarks on a 7-day branching adventure. Your child's choices shape the story — no two adventures are the same.",
    color: COLORS.secondary,
  },
  {
    num: "04",
    icon: <IconCamera />,
    title: "Drop into real life",
    desc: "Parents snap a photo at work, on vacation, anywhere. The creature gets composited into it. A push notification arrives: your creature went somewhere today.",
    color: COLORS.rose,
  },
];

const FEATURES = [
  {
    icon: <IconCamera />,
    title: "Photo Drops",
    desc: "Snap a photo anywhere — at work, the park, a cafe. Your child's creature appears in it, like magic. Send via app, email, or text.",
    color: COLORS.primary,
  },
  {
    icon: <IconChat />,
    title: "Creature Chat",
    desc: "Kids can talk to their creature. It stays in character, helps with homework (without giving answers), and has moody days for empathy building.",
    color: COLORS.secondary,
  },
  {
    icon: <IconBook />,
    title: "Branching Stories",
    desc: "7-day adventures with binary choices that change the outcome. Daily or self-paced. Every arc is unique — powered by AI, guided by your child.",
    color: COLORS.accent,
  },
  {
    icon: <IconBell />,
    title: "Creature Reminders",
    desc: "\"Time to brush your teeth!\" — but it's from their creature, in their creature's voice. Scheduled reminders parents configure from the dashboard.",
    color: COLORS.rose,
  },
  {
    icon: <IconHeart />,
    title: "Emotional Growth",
    desc: "Creatures have \"down days\" to teach empathy. Chat adapts to age group — silly for littles, Socratic for adventurers, peer-like for teens.",
    color: COLORS.primary,
  },
  {
    icon: <IconShield />,
    title: "Parent Dashboard",
    desc: "See mood summaries, arc progress, and drop history — never full chat transcripts. Manage reminders, age settings, and subscriptions in one place.",
    color: COLORS.secondary,
  },
];

const SAFETY_POINTS = [
  "COPPA-compliant with verified parental consent",
  "Multi-layered content safety: regex screening + AI safety filters",
  "Creature never breaks character or acknowledges being AI",
  "No violence, drugs, sexual content, or frightening material",
  "90-day chat retention with automatic purging",
  "Full data export and deletion on request",
  "Chat rate limiting prevents overuse",
  "Age-appropriate tone adapts to child's age group",
];

const PLANS = [
  {
    name: "Free Trial",
    price: "$0",
    period: "7 days",
    desc: "See what Wanderling is all about",
    features: ["1 scene per day", "3 photo drops", "Full creature chat", "1 complete story arc"],
    cta: "Start Free Trial",
    featured: false,
  },
  {
    name: "Adventure",
    price: "$7.99",
    period: "/month",
    desc: "The full Wanderling experience",
    features: ["1 scene per day", "10 drops per month", "Unlimited story arcs", "1 arc video per month", "Creature reminders", "Birthday scenes"],
    cta: "Get Started",
    featured: true,
  },
  {
    name: "Explorer",
    price: "$13.99",
    period: "/month",
    desc: "For the most dedicated adventurers",
    features: ["2 scenes per day", "25 drops per month", "Unlimited story arcs", "2 arc videos per month", "Everything in Adventure", "Priority image generation"],
    cta: "Get Started",
    featured: false,
  },
];

const FAQ_ITEMS = [
  {
    q: "What ages is Wanderling designed for?",
    a: "Wanderling is designed for children ages 4–17, with three age-adapted tiers: \"Littles\" (4–7) get simple words and silliness, \"Adventurers\" (8–12) get richer vocabulary and Socratic learning, and \"Wanderers\" (13+) get peer-like conversation and real-world themes.",
  },
  {
    q: "Is my child's data safe?",
    a: "Absolutely. We're COPPA-compliant with verified parental consent for under-13s. Chat messages auto-delete after 90 days. We never share personal data with third parties for advertising. Parents can export or delete all data at any time.",
  },
  {
    q: "How do Photo Drops work?",
    a: "Parents take a photo anywhere — at the office, a restaurant, on vacation. Upload it through the app, email it, or text it. Our AI composites the child's creature into the photo with a fun caption. The child gets a push notification that their creature went on an adventure.",
  },
  {
    q: "Can my child chat with their creature?",
    a: "Yes! The creature stays fully in character and adapts its tone to the child's age group. It can help with homework using Socratic questioning (never giving direct answers), tell stories, and even have occasional \"down days\" to help kids practice empathy.",
  },
  {
    q: "What happens after the free trial?",
    a: "After 7 days, you'll need an Adventure ($7.99/mo) or Explorer ($13.99/mo) plan to continue. Your child's creature and story progress are preserved — pick up right where you left off.",
  },
  {
    q: "Do I need to be artistic to draw a creature?",
    a: "Not at all! The messier and more creative the doodle, the more unique the creature. Stick figures, scribbles, and wild shapes all work. The AI interprets the intent, not the technique.",
  },
];

/* ─── Component ─── */
export default function Landing() {
  const navigate = useNavigate();

  const handleCTA = () => navigate("/get-started");

  return (
    <div className="min-h-screen bg-bg overflow-x-hidden">
      {/* ═══ NAV ═══ */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-bg/80 backdrop-blur-xl border-b border-dark/[0.04]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                <path d="M16 4C10 4 6 8.5 6 14c0 3.5 1.5 6 4 8l6 6 6-6c2.5-2 4-4.5 4-8 0-5.5-4-10-10-10z" fill="white" opacity="0.9" />
                <circle cx="12" cy="13" r="1.5" fill="var(--color-primary)" />
                <circle cx="20" cy="13" r="1.5" fill="var(--color-primary)" />
                <path d="M13 18c1.5 1.5 4.5 1.5 6 0" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <span className="font-display text-xl text-dark">Wanderling</span>
          </div>
          <div className="hidden sm:flex items-center gap-8">
            <a href="#how-it-works" className="font-body text-sm text-dark/50 hover:text-dark transition-colors">How it works</a>
            <a href="#features" className="font-body text-sm text-dark/50 hover:text-dark transition-colors">Features</a>
            <a href="#safety" className="font-body text-sm text-dark/50 hover:text-dark transition-colors">Safety</a>
            <a href="#pricing" className="font-body text-sm text-dark/50 hover:text-dark transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/kid-login")}
              className="font-heading text-sm font-semibold text-dark/50 hover:text-dark/70 transition-colors hidden sm:block"
            >
              I'm a Kid
            </button>
            <button
              onClick={() => navigate("/login")}
              className="font-heading text-sm font-semibold text-dark/50 hover:text-dark/70 transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={handleCTA}
              className="bg-dark text-white font-heading font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-dark/90 transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="pt-28 pb-20 sm:pt-36 sm:pb-28 px-6 relative">
        {/* Background orbs */}
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-primary/[0.05] blur-3xl pointer-events-none" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-secondary/[0.05] blur-3xl pointer-events-none" />
        <div className="absolute top-[40%] left-[20%] w-[250px] h-[250px] rounded-full bg-accent/[0.05] blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — text + CTA */}
            <div className="animate-spring-in">
              <div className="inline-flex items-center gap-2 bg-primary/[0.08] text-primary font-heading font-semibold text-xs tracking-wide uppercase px-4 py-2 rounded-full mb-6">
                <span className="w-1.5 h-1.5 bg-primary rounded-full animate-glow" />
                Ages 4–17 &middot; COPPA Compliant
              </div>

              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-dark tracking-tight leading-[0.95] mb-6">
                Your doodle.
                <br />
                <span className="text-primary">Its adventure.</span>
              </h1>

              <p className="font-body text-lg text-dark/50 max-w-lg leading-relaxed mb-10">
                A child draws a creature. AI brings it to life as a photorealistic character
                with a name, backstory, and personality — then sends it on a 7-day branching
                adventure. Parents "drop" real-world photos and the creature appears in them.
              </p>

              <div className="flex flex-col sm:flex-row items-start gap-4">
                <button
                  onClick={() => navigate("/try")}
                  className="group bg-dark text-white font-heading font-bold text-base px-10 py-4 rounded-2xl transition-all hover:shadow-xl hover:shadow-dark/10 active:scale-[0.97]"
                >
                  <span className="flex items-center gap-2">
                    Try It Free — See Your Doodle Come Alive
                    <IconArrow />
                  </span>
                </button>
                <button
                  onClick={handleCTA}
                  className="font-heading text-sm font-semibold text-dark/50 hover:text-dark/70 transition-colors py-2 sm:self-center"
                >
                  or sign up directly &rarr;
                </button>
              </div>
              <span className="font-body text-xs text-dark/30 mt-3 block">No account needed &middot; One free preview per device</span>
              <a href="#companion" className="font-heading text-sm font-bold text-accent/70 hover:text-accent transition-colors mt-2 inline-flex items-center gap-1.5">
                Not a kid? It's for everyone.
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="mt-px"><path d="M7 3v8M4 8l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </a>
            </div>

            {/* Right — before/after transformation */}
            <div className="relative animate-spring-in" style={{ animationDelay: "0.15s" }}>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] to-accent/[0.03] rounded-3xl blur-2xl scale-110" />
              <div className="relative flex flex-col gap-4">
                {/* Before — the doodle */}
                <div className="relative">
                  <div className="card overflow-hidden flex flex-col sm:flex-row items-center gap-0">
                    <img
                      src="/before.jpg"
                      alt="A child's hand-drawn doodle of a creature"
                      className="w-full sm:w-48 sm:h-48 h-48 object-cover shrink-0 rounded-t-[20px] sm:rounded-t-none sm:rounded-l-[20px]"
                    />
                    <div className="p-4 sm:p-5">
                      <span className="font-heading text-[10px] font-bold tracking-[0.15em] uppercase text-dark/30">Step 1</span>
                      <p className="font-display text-base sm:text-lg text-dark mt-0.5">A kid draws a doodle</p>
                      <p className="font-body text-xs text-dark/40 mt-1 leading-relaxed">On the canvas or photographed from paper. Stick figures welcome.</p>
                    </div>
                  </div>
                </div>

                {/* Connecting arrow */}
                <div className="flex justify-center -my-1 relative z-10">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-lg shadow-primary/20 animate-glow">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M12 5v14M6 13l6 6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>

                {/* After — the Wanderling */}
                <div className="relative">
                  <div className="card overflow-hidden ring-2 ring-primary/10 shadow-xl shadow-primary/[0.06]">
                    <div className="relative">
                      <img
                        src="/after.png"
                        alt="The doodle transformed into a photorealistic AI-generated creature"
                        className="w-full aspect-[4/3] object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark/30 via-transparent to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="font-heading text-[10px] font-bold tracking-[0.15em] uppercase text-white/60">The Wanderling</span>
                        <p className="font-display text-lg sm:text-xl text-white mt-0.5">Brought to life by AI</p>
                      </div>
                    </div>
                  </div>
                  {/* Sparkle decorations */}
                  <div className="absolute -top-2 -left-2 w-3 h-3 text-accent animate-sparkle" style={{ animationDelay: "0.3s" }}>
                    <svg viewBox="0 0 12 12" fill="currentColor"><path d="M6 0l1.5 4.5L12 6l-4.5 1.5L6 12l-1.5-4.5L0 6l4.5-1.5z" /></svg>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 text-primary animate-sparkle" style={{ animationDelay: "1.2s" }}>
                    <svg viewBox="0 0 12 12" fill="currentColor"><path d="M6 0l1.5 4.5L12 6l-4.5 1.5L6 12l-1.5-4.5L0 6l4.5-1.5z" /></svg>
                  </div>
                  <div className="absolute top-[40%] -right-3 w-2 h-2 text-secondary animate-sparkle" style={{ animationDelay: "2s" }}>
                    <svg viewBox="0 0 12 12" fill="currentColor"><path d="M6 0l1.5 4.5L12 6l-4.5 1.5L6 12l-1.5-4.5L0 6l4.5-1.5z" /></svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" className="py-20 sm:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-primary/60 mb-3 block">How it works</span>
            <h2 className="font-display text-3xl sm:text-5xl text-dark tracking-tight">
              From doodle to daily magic
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step) => (
              <div key={step.num} className="group relative">
                <div className="card p-8 h-full transition-all hover:shadow-lg hover:shadow-dark/[0.04] hover:-translate-y-1">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: step.color.bg, color: step.color.text }}>
                      {step.icon}
                    </div>
                    <span className="font-heading text-3xl font-bold text-dark/[0.06]">{step.num}</span>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-dark mb-3">{step.title}</h3>
                  <p className="font-body text-sm text-dark/50 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PHOTO DROPS — EMOTIONAL CORE ═══ */}
      <section className="py-20 sm:py-28 px-6 bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-rose/60 mb-3 block">The magic moment</span>
              <h2 className="font-display text-3xl sm:text-5xl text-dark tracking-tight mb-6 leading-tight">
                "Look — my creature went to work with Dad!"
              </h2>
              <p className="font-body text-base sm:text-lg text-dark/50 leading-relaxed mb-8">
                Photo Drops are the heart of Wanderling. Parents snap a photo anywhere — at the office,
                a restaurant, on vacation. Upload it, and your child's creature appears in the scene.
                A push notification arrives. Pure joy.
              </p>
              <div className="space-y-4">
                {["Send drops via app, email, or text message", "AI composites the creature seamlessly into any photo", "Creature-voiced captions add narrative magic", "Build a memory gallery of real-world adventures"].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="mt-0.5 w-5 h-5 rounded-full bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
                      <IconCheck />
                    </div>
                    <span className="font-body text-sm text-dark/60">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Drop mockup */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.06] to-accent/[0.04] rounded-3xl blur-2xl" />
              <div className="relative card p-4 sm:p-6">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="/office.jpg"
                    alt="Bubbles the wanderling throwing paperclips at Dad while he works"
                    className="w-full aspect-[4/3] object-cover"
                  />
                </div>
                <div className="mt-4 px-2">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 shrink-0" />
                    <div>
                      <p className="font-heading text-sm font-semibold text-dark">Bubbles visited Dad at work!</p>
                      <p className="font-body text-xs text-dark/40 mt-1">"Had SO much fun playing with Dad today! He was trying to do something on his computer but I think the paperclips were more interesting. He seemed a little annoyed though..."</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="features" className="py-20 sm:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-secondary/60 mb-3 block">Features</span>
            <h2 className="font-display text-3xl sm:text-5xl text-dark tracking-tight mb-4">
              Everything a creature needs
            </h2>
            <p className="font-body text-base text-dark/40 max-w-xl mx-auto">
              Built for kids to love and parents to trust.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="card p-8 transition-all hover:shadow-lg hover:shadow-dark/[0.04] hover:-translate-y-1">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: f.color.bg, color: f.color.text }}>
                  {f.icon}
                </div>
                <h3 className="font-heading text-lg font-bold text-dark mb-3">{f.title}</h3>
                <p className="font-body text-sm text-dark/50 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SAFETY ═══ */}
      <section id="safety" className="py-20 sm:py-28 px-6 bg-gradient-to-b from-transparent via-secondary/[0.02] to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Shield graphic */}
            <div className="order-2 lg:order-1 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-secondary/10 to-primary/5 rounded-full blur-3xl scale-150" />
                <div className="relative w-48 h-56 sm:w-60 sm:h-72">
                  {/* Shield shape */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 200 240" fill="none">
                      <path d="M100 10l85 35v70c0 55-38 95-85 115C53 210 15 170 15 115V45l85-35z"
                        fill="var(--color-secondary)" opacity="0.08" />
                      <path d="M100 30l65 27v55c0 42-29 73-65 88-36-15-65-46-65-88V57l65-27z"
                        fill="var(--color-secondary)" opacity="0.06" />
                      <path d="M80 125l20 20 40-50" stroke="var(--color-secondary)" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" opacity="0.3" />
                    </svg>
                  </div>
                  {/* Orbiting badges */}
                  <div className="absolute top-4 right-0 bg-card rounded-xl px-3 py-1.5 shadow-md shadow-dark/[0.04] animate-float" style={{ animationDelay: "0s" }}>
                    <span className="font-heading text-[10px] font-bold text-secondary tracking-wide uppercase">COPPA</span>
                  </div>
                  <div className="absolute bottom-12 left-0 bg-card rounded-xl px-3 py-1.5 shadow-md shadow-dark/[0.04] animate-float" style={{ animationDelay: "1.2s" }}>
                    <span className="font-heading text-[10px] font-bold text-primary tracking-wide uppercase">SB-1381</span>
                  </div>
                  <div className="absolute top-1/3 -left-4 bg-card rounded-xl px-3 py-1.5 shadow-md shadow-dark/[0.04] animate-float" style={{ animationDelay: "0.6s" }}>
                    <span className="font-heading text-[10px] font-bold text-accent tracking-wide uppercase">Encrypted</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <span className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-secondary/60 mb-3 block">Safety first</span>
              <h2 className="font-display text-3xl sm:text-5xl text-dark tracking-tight mb-6 leading-tight">
                Built for children.<br />Trusted by parents.
              </h2>
              <p className="font-body text-base text-dark/50 leading-relaxed mb-8">
                Every layer of Wanderling is designed with child safety at its core. From AI content filters
                to parental controls, we take protecting your family seriously.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {SAFETY_POINTS.map((point) => (
                  <div key={point} className="flex items-start gap-2.5">
                    <div className="mt-1 w-4 h-4 rounded-full bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
                      <svg width="10" height="10" viewBox="0 0 18 18" fill="none">
                        <path d="M5 9l3 3 5-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <span className="font-body text-sm text-dark/55 leading-snug">{point}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <a href="/safety" className="font-heading text-sm font-semibold text-secondary hover:text-secondary/80 transition-colors inline-flex items-center gap-1">
                  Read our safety whitepaper
                  <IconArrow />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" className="py-20 sm:py-28 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-accent/60 mb-3 block">Pricing</span>
            <h2 className="font-display text-3xl sm:text-5xl text-dark tracking-tight mb-4">
              Simple, honest pricing
            </h2>
            <p className="font-body text-base text-dark/40 max-w-lg mx-auto">
              Start free. Upgrade when your creature's adventures deserve more.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`card p-8 relative transition-all hover:-translate-y-1 ${
                  plan.featured
                    ? "ring-2 ring-primary/20 shadow-lg shadow-primary/[0.06]"
                    : "hover:shadow-lg hover:shadow-dark/[0.04]"
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white font-heading text-[10px] font-bold tracking-widest uppercase px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="font-heading text-base font-bold text-dark mb-1">{plan.name}</h3>
                  <p className="font-body text-xs text-dark/40">{plan.desc}</p>
                </div>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-display text-4xl text-dark">{plan.price}</span>
                  <span className="font-body text-sm text-dark/40">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <div className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${plan.featured ? "bg-primary/10 text-primary" : "bg-dark/[0.04] text-dark/30"}`}>
                        <svg width="10" height="10" viewBox="0 0 18 18" fill="none">
                          <path d="M5 9l3 3 5-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <span className="font-body text-sm text-dark/55">{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={handleCTA}
                  className={`w-full py-3 rounded-xl font-heading font-bold text-sm transition-all active:scale-[0.97] ${
                    plan.featured
                      ? "bg-dark text-white hover:bg-dark/90 shadow-lg shadow-dark/10"
                      : "bg-dark/[0.04] text-dark hover:bg-dark/[0.08]"
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>

          <p className="text-center font-body text-xs text-dark/30 mt-8">
            Need more drops? Add a drop pack anytime: 10 for $1.99, 25 for $3.99, 50 for $6.99, or 100 for $11.99.
            <br />
            Want a video recap of every completed arc? Add the video add-on for $5/mo.
          </p>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="py-20 sm:py-28 px-6 bg-gradient-to-b from-transparent via-accent/[0.02] to-transparent">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-dark/30 mb-3 block">FAQ</span>
            <h2 className="font-display text-3xl sm:text-4xl text-dark tracking-tight">
              Questions parents ask
            </h2>
          </div>

          <div className="space-y-4">
            {FAQ_ITEMS.map((item) => (
              <details key={item.q} className="card group">
                <summary className="cursor-pointer px-8 py-6 flex items-center justify-between gap-4 list-none [&::-webkit-details-marker]:hidden">
                  <span className="font-heading text-base font-semibold text-dark">{item.q}</span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    className="shrink-0 text-dark/20 transition-transform group-open:rotate-45"
                  >
                    <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </summary>
                <div className="px-8 pb-6 -mt-2">
                  <p className="font-body text-sm text-dark/50 leading-relaxed">{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ COMPANION CTA ═══ */}
      <section id="companion" className="py-20 sm:py-28 px-6 bg-gradient-to-b from-transparent via-accent/[0.03] to-transparent scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="card p-10 sm:p-14 relative overflow-hidden">
            <div className="absolute top-[-20%] right-[-10%] w-[300px] h-[300px] rounded-full bg-accent/[0.06] blur-3xl pointer-events-none" />
            <div className="absolute bottom-[-20%] left-[-10%] w-[250px] h-[250px] rounded-full bg-secondary/[0.06] blur-3xl pointer-events-none" />

            <div className="relative z-10 grid sm:grid-cols-2 gap-8 items-center">
              <div>
                <span className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-accent/60 mb-3 block">Not just for kids</span>
                <h2 className="font-display text-2xl sm:text-3xl text-dark tracking-tight mb-4 leading-tight">
                  A playful companion for&nbsp;you
                </h2>
                <p className="font-body text-sm text-dark/50 leading-relaxed mb-6">
                  A Wanderling can be a way to stay connected with someone you care about. Create one together
                  and surprise each other with photo drops from your day. Perfect for couples, friends, or anyone
                  who wants a playful "thinking of you" moment.
                </p>
                <button
                  onClick={() => navigate("/signup?flow=companion")}
                  className="group bg-dark text-white font-heading font-bold text-sm px-8 py-3.5 rounded-2xl transition-all hover:shadow-lg hover:shadow-dark/10 active:scale-[0.97]"
                >
                  <span className="flex items-center gap-2">
                    Create Your Companion
                    <IconArrow />
                  </span>
                </button>
              </div>
              <div className="flex justify-center">
                <div className="w-40 h-40 bg-gradient-to-br from-accent/15 to-secondary/10 rounded-[40%_40%_45%_45%] relative">
                  <div className="absolute top-[30%] left-[22%] w-4 h-4 bg-dark/12 rounded-full" />
                  <div className="absolute top-[30%] right-[22%] w-4 h-4 bg-dark/12 rounded-full" />
                  <div className="absolute bottom-[22%] left-1/2 -translate-x-1/2 w-10 h-4 bg-dark/8 rounded-full" />
                  <div className="absolute -top-2 -right-2 w-3 h-3 text-accent animate-sparkle" style={{ animationDelay: "0.5s" }}>
                    <svg viewBox="0 0 12 12" fill="currentColor"><path d="M6 0l1.5 4.5L12 6l-4.5 1.5L6 12l-1.5-4.5L0 6l4.5-1.5z" /></svg>
                  </div>
                  <div className="absolute -bottom-1 -left-1 w-2 h-2 text-secondary animate-sparkle" style={{ animationDelay: "1.5s" }}>
                    <svg viewBox="0 0 12 12" fill="currentColor"><path d="M6 0l1.5 4.5L12 6l-4.5 1.5L6 12l-1.5-4.5L0 6l4.5-1.5z" /></svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ RETURNING USERS ═══ */}
      <section className="pb-10 px-6">
        <div className="max-w-md mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
          <button
            onClick={() => navigate("/kid-login")}
            className="font-heading text-sm font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            I'm a Kid &mdash; Log Back In
          </button>
          <span className="hidden sm:block text-dark/15">|</span>
          <button
            onClick={() => navigate("/login")}
            className="font-heading text-sm font-semibold text-dark/40 hover:text-dark/60 transition-colors"
          >
            Already have an account? Sign in
          </button>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-20 sm:py-28 px-6">
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/[0.04] via-accent/[0.03] to-secondary/[0.04] rounded-3xl blur-2xl" />
          <div className="relative card p-12 sm:p-16">
            <div className="animate-float inline-block mb-8">
              <div className="w-20 h-22 bg-gradient-to-br from-primary/15 to-accent/10 rounded-[40%_40%_45%_45%] mx-auto relative">
                <div className="absolute top-[35%] left-[25%] w-3 h-3 bg-dark/15 rounded-full" />
                <div className="absolute top-[35%] right-[25%] w-3 h-3 bg-dark/15 rounded-full" />
                <div className="absolute bottom-[25%] left-1/2 -translate-x-1/2 w-8 h-3 bg-dark/10 rounded-full" />
              </div>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl text-dark tracking-tight mb-4">
              Every doodle deserves a life
            </h2>
            <p className="font-body text-base text-dark/45 max-w-md mx-auto mb-10">
              Start your free 7-day trial. No credit card required. Your child's creature is waiting to be born.
            </p>
            <button
              onClick={handleCTA}
              className="group bg-dark text-white font-heading font-bold text-base px-10 py-4 rounded-2xl transition-all hover:shadow-xl hover:shadow-dark/10 active:scale-[0.97]"
            >
              <span className="flex items-center gap-2">
                Create Your Wanderling
                <IconArrow />
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="py-12 px-6 border-t border-dark/[0.04]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center">
                <svg width="14" height="14" viewBox="0 0 32 32" fill="none">
                  <path d="M16 4C10 4 6 8.5 6 14c0 3.5 1.5 6 4 8l6 6 6-6c2.5-2 4-4.5 4-8 0-5.5-4-10-10-10z" fill="white" opacity="0.9" />
                </svg>
              </div>
              <span className="font-display text-base text-dark">Wanderling</span>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
              <a href="/privacy" className="font-body text-xs text-dark/35 hover:text-dark/60 transition-colors">Privacy</a>
              <a href="/terms" className="font-body text-xs text-dark/35 hover:text-dark/60 transition-colors">Terms</a>
              <a href="/safety" className="font-body text-xs text-dark/35 hover:text-dark/60 transition-colors">Safety</a>
              <a href="#pricing" className="font-body text-xs text-dark/35 hover:text-dark/60 transition-colors">Pricing</a>
              <a href="#faq" className="font-body text-xs text-dark/35 hover:text-dark/60 transition-colors">FAQ</a>
              <a href="/login" className="font-body text-xs text-dark/35 hover:text-dark/60 transition-colors">Sign In</a>
            </div>

            <p className="font-body text-xs text-dark/25">
              &copy; {new Date().getFullYear()} Wanderling
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
